import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const interests = [
  "Formula One",
  "Football",
  "Brand Partnership",
  "Rights Holder",
  "Hospitality & Experiences",
  "Other",
];

const initial = { name: "", company: "", email: "", interest: "", message: "" };

const inputClass =
  "w-full bg-transparent border-b border-black/20 focus:border-[#0A0A0A] outline-none py-3 text-base font-light text-[#0A0A0A] placeholder:text-neutral-400 transition-colors duration-200";

export default function ContactSection() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.interest) {
      toast.error("Please select an area of interest.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — your message has been received. We'll be in touch shortly.");
      setForm(initial);
    } catch {
      toast.error("Something went wrong. Please email partnerships@qualive.co.uk directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="bg-[#F7F6F2] text-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-28 lg:py-36 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
        <div>
          <p className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#0A0A0A] mb-6">
            07 — Contact
          </p>
          <h2 className="font-heading font-light uppercase tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl">
            Start a conversation<span className="text-[#0A0A0A]">.</span>
          </h2>
          <p className="mt-8 text-base sm:text-lg font-light text-neutral-700 leading-relaxed max-w-md">
            Whether you are a brand exploring sport or a rights holder looking
            for the right commercial partner, speak with QUALIVE.
          </p>
          <a
            href="mailto:partnerships@qualive.co.uk"
            data-testid="contact-direct-email"
            className="group inline-flex items-center gap-2 mt-12 font-heading font-light text-2xl sm:text-3xl tracking-tight text-[#0A0A0A] hover:text-[#0A0A0A] transition-colors duration-200"
          >
            partnerships@qualive.co.uk
            <ArrowUpRight size={22} className="text-[#0A0A0A] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" />
          </a>
        </div>

        <form data-testid="contact-form" onSubmit={submit} className="flex flex-col gap-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <input
              data-testid="contact-input-name"
              required
              placeholder="Name"
              value={form.name}
              onChange={set("name")}
              className={inputClass}
            />
            <input
              data-testid="contact-input-company"
              placeholder="Company"
              value={form.company}
              onChange={set("company")}
              className={inputClass}
            />
          </div>
          <input
            data-testid="contact-input-email"
            required
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={set("email")}
            className={inputClass}
          />
          <select
            data-testid="contact-select-interest"
            required
            value={form.interest}
            onChange={set("interest")}
            className={`${inputClass} cursor-pointer ${form.interest ? "" : "text-neutral-400"}`}
          >
            <option value="" disabled>
              Area of Interest
            </option>
            {interests.map((i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
          </select>
          <textarea
            data-testid="contact-input-message"
            placeholder="Message (optional)"
            rows={4}
            value={form.message}
            onChange={set("message")}
            className={`${inputClass} resize-none`}
          />
          <button
            data-testid="contact-submit-button"
            type="submit"
            disabled={sending}
            className="self-start font-mono-tech text-xs uppercase tracking-[0.2em] bg-[#0A0A0A] text-[#F7F6F2] px-10 py-4 hover:bg-neutral-700 disabled:opacity-50 transition-colors duration-200"
          >
            {sending ? "Sending…" : "Start a Conversation"}
          </button>
        </form>
      </div>
    </section>
  );
}
